import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight, Facebook, Instagram, Youtube } from "lucide-react";
import SectionEyebrow from "@/components/SectionEyebrow";
import { CONTACT, IMAGES } from "@/data/content";
import api from "@/lib/api";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé. Nous reviendrons vers vous rapidement.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        <img src={IMAGES.community} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/20" />
        <div className="relative mv-container py-24 text-white">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-white/80" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold">Contact</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Parlons ensemble.
          </h1>
        </div>
      </section>

      <section className="mv-section bg-white">
        <div className="mv-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>Informations de contact</SectionEyebrow>
            <h2 className="mt-5 font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1C1C1C]">
              N'hésitez pas à nous contacter.
            </h2>
            <p className="mt-5 text-base text-muted leading-relaxed">
              N'hésitez pas à nous contacter pour toute question, suggestion ou demande de partenariat.
            </p>

            <ul className="mt-10 space-y-6">
              <li className="flex gap-4">
                <div className="h-11 w-11 bg-[#0B3D91] flex items-center justify-center shrink-0">
                  <Mail size={18} className="text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B3D91]">Email</div>
                  <a href={`mailto:${CONTACT.email}`} className="mt-1 block text-lg font-bold text-[#1C1C1C] hover:text-[#0B3D91]">
                    {CONTACT.email}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="h-11 w-11 bg-[#0B3D91] flex items-center justify-center shrink-0">
                  <Phone size={18} className="text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B3D91]">Téléphone</div>
                  <a href={`tel:${CONTACT.phone}`} className="mt-1 block text-lg font-bold text-[#1C1C1C] hover:text-[#0B3D91]">
                    {CONTACT.phone}
                  </a>
                  <div className="text-sm text-muted mt-1">WhatsApp: {CONTACT.whatsapp}</div>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="h-11 w-11 bg-[#0B3D91] flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B3D91]">Adresse</div>
                  <div className="mt-1 text-lg font-bold text-[#1C1C1C]">{CONTACT.address}</div>
                  <div className="text-sm text-muted">{CONTACT.country}</div>
                </div>
              </li>
            </ul>

            <div className="mt-10 flex gap-3">
              <a href="#" aria-label="Facebook" className="h-11 w-11 bg-[#1C1C1C] text-white hover:bg-[#0B3D91] transition-colors flex items-center justify-center">
                <Facebook size={18} />
              </a>
              <a href="#" aria-label="Instagram" className="h-11 w-11 bg-[#1C1C1C] text-white hover:bg-[#0B3D91] transition-colors flex items-center justify-center">
                <Instagram size={18} />
              </a>
              <a href="#" aria-label="YouTube" className="h-11 w-11 bg-[#1C1C1C] text-white hover:bg-[#0B3D91] transition-colors flex items-center justify-center">
                <Youtube size={18} />
              </a>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 bg-[#F8FAFC] p-8 lg:p-12 border border-gray-200" data-testid="contact-form">
            <h2 className="font-heading font-black text-2xl text-[#1C1C1C]">Écrivez-nous</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="label-eyebrow">Nom</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
              <div>
                <label className="label-eyebrow">Email</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
              <div>
                <label className="label-eyebrow">Téléphone (facultatif)</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="contact-phone"
                  className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
              <div>
                <label className="label-eyebrow">Sujet</label>
                <input
                  required
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-subject"
                  className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-eyebrow">Message</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-8 btn-primary disabled:opacity-60"
            >
              {loading ? "Envoi..." : "Envoyer le message"} <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
